/**
 * The decisions the popup call window makes, as pure functions (MSPPROC-19105).
 *
 * The window was built for exactly one call: refreshing it, or reopening it for the same number,
 * landed on "Call already placed — close it and start a new call from the ticket". Masood wanted
 * to call a customer back who had not answered and was told to close the window.
 *
 * The automatic guard stays exactly as it is — a refresh must never silently redial a customer.
 * What is added is the ability to dial again ON PURPOSE, from the same window.
 */

export type DialOnMountDecision =
  | 'no-target'
  | 'already-started'
  | 'already-dialed'
  | 'not-ready'
  | 'dial';

export interface IDialOnMountInput {
  target: string;
  alreadyStarted: boolean;
  windowHasDialed: boolean;
  twilioReady: boolean;
}

/**
 * Whether MOUNTING should place a call. Returns the reason rather than a boolean, because the
 * reasons are what the screen has to explain and a bare false cannot be rendered.
 *
 * `already-dialed` is the guard: this window has dialed this number once, so a refresh must not
 * do it again. An explicit click does not come through here at all — that is the whole point of
 * keeping the two paths separate.
 */
export const decideDialOnMount = (input: IDialOnMountInput): DialOnMountDecision => {
  if (input.alreadyStarted) {
    return 'already-started';
  }
  if (!input.target) {
    return 'no-target';
  }
  if (!input.twilioReady) {
    return 'not-ready';
  }
  return input.windowHasDialed ? 'already-dialed' : 'dial';
};

export interface IPopupCallSessionState {
  /** Whether the MOUNT path has run. Deliberately survives a reset — see resetForNextCall. */
  started: boolean;
  wasActive: boolean;
  endSignaled: boolean;
  activeCallId: number | undefined;
  startError: string | undefined;
  alreadyPlaced: boolean;
  documentationNotice: string | undefined;
}

/**
 * The state a SECOND call starts from.
 *
 * Everything the window recorded about the previous call is cleared. The window was written
 * assuming one call per lifetime, so each of these is set once and never unset — a leftover
 * `endSignaled` means the new call's end is never signalled, and a leftover `activeCallId`
 * attaches the new call's lifecycle, transcript and time entry to the PREVIOUS call's row. That
 * is far worse than the dead end being fixed.
 *
 * `started` is deliberately NOT cleared. Clearing it would re-arm the automatic dial-on-mount, which
 * is the protection Masood explicitly kept — only an explicit click may dial. The reset prepares
 * the window for a call the technician asks for; it does not re-open the door to an automatic one.
 *
 * Nothing here needs preserving for the previous call: its time-entry link lives in the database
 * against its own id, so a failed or unsubmitted entry survives independently of this window.
 */
export const resetForNextCall = (state: IPopupCallSessionState): IPopupCallSessionState => ({
  started: state.started,
  wasActive: false,
  endSignaled: false,
  activeCallId: undefined,
  startError: undefined,
  alreadyPlaced: false,
  documentationNotice: undefined,
});

export interface ICallAgainInput {
  callEnded: boolean;
  awaitingAutomaticPost: boolean;
  hasTarget: boolean;
}

/**
 * Whether to offer "Call again".
 *
 * Not while the previous call's time entry is still mid-post: starting a new call from a window
 * whose previous entry is in flight races the automatic trigger, which is the same defect the
 * hidden manual-submit control just closed, in a new costume.
 */
export const showsCallAgain = (input: ICallAgainInput): boolean =>
  input.callEnded && input.hasTarget && !input.awaitingAutomaticPost;
